/*
 *  This file hrone-network.interceptor.ts is part of HROne Inbox. The intelectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 20203
 *  (C) 2015-20203. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 *
 */

import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpHeaders, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { HroneNetworkHelperService } from './hrone-network-helper.service';
import { catchError, filter, finalize, switchMap, take, tap } from 'rxjs/operators';
import { HttpService } from '../http-service';
import { TLoginTokenResponse } from '../../models/common.models';
import { HroneIdleService } from '../hrone-idle.service';
import { Router } from '@angular/router';
import { NewRelicHandlerService } from '../newrelic-handler/newrelic-handler.service';




@Injectable()
export class HroneNetworkInterceptor implements HttpInterceptor {
  tokenSubject: BehaviorSubject<TLoginTokenResponse | null> = new BehaviorSubject(this._helper.getAuthLogin());

  constructor(
    private readonly _helper: HroneNetworkHelperService,
    private readonly _httpService: HttpService,
    private _hroneIdleService: HroneIdleService,
    private router: Router,
    private readonly _newRelic: NewRelicHandlerService
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const skipWithCredentials = request.headers.get('skip-withcredentials') === 'true';
    const clonedRequest = request.clone({
      withCredentials: !skipWithCredentials,
      setHeaders: {
        'X-Requested-With': window.location.origin
      },
      headers: request.headers.delete('skip-withcredentials')
    });
    const token = localStorage.getItem('authlogin');
    if (token && !this.router.url.includes('/login') && !request.url.includes('/Logout') && request.url?.split('/')?.splice(-1)?.[0] !== 'LogOff') {
      if (this._hroneIdleService.isScreenLockIdle) {
        let remainingIdleTime = localStorage.getItem('isAppLock') ? JSON.parse(localStorage.getItem('isAppLock')!) : false;
        if (!remainingIdleTime) {
          this._hroneIdleService.startWatchingActivity(this._hroneIdleService.isScreenLockIdle);
        }
      } else if (this._hroneIdleService.isApiCall) {
        localStorage.removeItem('isAppLock')
        this._hroneIdleService.stopUpdateLockTime();
      }
    }


    const requestStart = performance.now();

    return next.handle(clonedRequest).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse && this._newRelic.isEnabled) {
          const duration = Math.round(performance.now() - requestStart);
          this._newRelic.addPageAction('HttpSuccess', {
            httpMethod: request.method,
            apiUrl: request.url,
            statusCode: event.status,
            duration,
            pageUrl: window.location.href,
            pagePath: window.location.pathname
          });
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (this._newRelic.isEnabled) {
          const duration = Math.round(performance.now() - requestStart);
          const apiUrl = error.url ?? request.url;
          let apiEndpoint = apiUrl;
          try { apiEndpoint = new URL(apiUrl).pathname; } catch {}
          const serverError = error.error?.error || error.error?.message || error.error;
          const attrs = {
            errorType: 'HttpError',
            httpMethod: request.method,
            apiUrl,
            apiEndpoint,
            statusCode: error.status,
            statusText: error.statusText,
            errorMessage: error.message,
            serverErrorMessage: typeof serverError === 'string' ? serverError : JSON.stringify(serverError ?? ''),
            duration,
            pageUrl: window.location.href,
            pagePath: window.location.pathname,
            timestamp: new Date().toISOString()
          };
          this._newRelic.addPageAction('HttpError', attrs);
          this._newRelic.noticeError(error, attrs);
          this._newRelic.log(`HTTP ${error.status} ${request.method} ${apiUrl}`, attrs);
        }
        if (request.headers.get('hrone-refresh-header')) {
          if (error.status === 503) return throwError(error)
          const newError = { ...error };
          newError.status = 5999;
          return throwError(newError);
        }
        if ((error.status === 403 && (error?.url || "").includes(this._helper._config.base_url)) ||
            ((error?.url || "").includes(this._helper._config.microservice_base_url) && error.status === 401) ||
            ((error?.url || "").includes(this._helper._config.legoBaseUrl) && error.status === 401)) {
          return this.handle403Error(request, next);
        }
        return throwError(error);
      })
    );
  }


  handle403Error(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (!this.getIsRefreshing()) {

      this.setIsRefreshing();
      this.tokenSubject.next(null);

      return this._helper.getRefreshToken().pipe(
        switchMap((newToken: TLoginTokenResponse) => {
          this._helper.setLoginAuth(newToken);
          this._httpService.updateSignalRAuthTokenSubject.next({ token: newToken, isLogout: false });
          this.tokenSubject.next(newToken);
          const authlogin = JSON.parse(localStorage.getItem('authlogin') || '{}');
          const accessToken = authlogin ? authlogin.access_token : '';
          const dc: string = localStorage.getItem('domainCode') ? localStorage.getItem('domainCode')! : this._httpService._config.domainCode
          const modifiedHeaders = new HttpHeaders({
            'domainCode': dc,
            'AccessMode': 'W'
          });
          request.clone().headers.keys().forEach(key => {
            key !== 'Authorization' && modifiedHeaders.set(key, request.clone().headers.get(key)!)
          });
          const isLegoRequest = request.url.includes(this._helper._config.legoBaseUrl);
          const useChatHeader = request.headers.get('ischatheader') === 'true';
          const finalHeaders = isLegoRequest
            ? this.mergeOriginalHeaders(this._httpService.getLegoHeaders(), request)
            : useChatHeader
              ? this._httpService.chatHeader()
              : modifiedHeaders;
          const newRequest = request.clone({ headers: finalHeaders, withCredentials: true });
          return next.handle(newRequest);
        }),
        catchError((err: HttpErrorResponse) => {
          if (this._newRelic.isEnabled) {
            this._newRelic.noticeError(err, {
              url: err.url ?? request.url,
              status: err.status,
              method: request.method,
              context: 'token-refresh'
            });
            this._newRelic.log(`Token refresh failed: HTTP ${err.status} ${request.method} ${err.url ?? request.url}`, {
              status: err.status,
              method: request.method,
              url: err.url ?? request.url,
              context: 'token-refresh'
            });
          }
          if (err.url?.includes("/oauth2/token")) {
            if (err.status === 503) return throwError(err)
            this.removeIsRefreshing()
            this._helper.logout();
            const newError = { ...err };
            newError.status = 5999;
            return throwError(newError);
          }
          return throwError(err);
        }),
        finalize(() => {

          this.removeIsRefreshing()
        })
      );


    } else {
      return this.tokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(() => {
          const isLegoRequest = request.url.includes(this._helper._config.legoBaseUrl);
          const useChatHeader = request.headers.get('ischatheader') === 'true';
          const headers = isLegoRequest
            ? this.mergeOriginalHeaders(this._httpService.getLegoHeaders(), request)
            : useChatHeader
              ? this._httpService.chatHeader()
              : this._httpService.getHeaders();
          const newRequest = request.clone({ headers, withCredentials: true });
          return next.handle(newRequest);
        })
      );
    }
  }

  removeIsRefreshing(): void {
    localStorage.removeItem("isRefreshingToken");
  }

  setIsRefreshing(): void {
    localStorage.setItem("isRefreshingToken", JSON.stringify(true));
  }

  getIsRefreshing(): boolean {
    return JSON.parse(localStorage.getItem("isRefreshingToken") || 'false');
  }

/**
 * Add the extra headers from the original request during token-refresh retry.
 * Keep the fresh base headers if the same key already exists.
 */
  private mergeOriginalHeaders(base: HttpHeaders, original: HttpRequest<any>): HttpHeaders {
    let merged = base;
    original.headers.keys().forEach(key => {
      if (key.toLowerCase() === 'authorization' || merged.has(key)) return;
      merged = merged.set(key, original.headers.get(key)!);
    });
    return merged;
  }

}


